import React, { useContext, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { API } from "aws-amplify";
import {
  Box,
  Button,
  IconButton,
  InputLabel,
  Stack,
  Typography,
} from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useForm } from "react-hook-form";
import { DialogWindow } from "UI/DialogWindow";
import { FormTextField } from "UI/FormTextField";
import { FormSelectField } from "UI/FormSelectField";
import {
  getPasswordConfirmValidation,
  emailValidation,
  passwordValidation,
  nameValidation,
  required,
  phoneNumberValidation,
} from "models/validations";
import { GlobalContext } from "store/globalContext";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { RequiredMark } from "UI/RequiredMark";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { AddNewRegionDialog } from "./AddNewRegionDialog";
import { useCloseAndReset } from "hooks/useCloseAndReset";

/** @type {import("types/commonTypes").NewUserData}*/
const defaultFormData = {
  role: "",
  organizationId: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  password_repeat: "",
  jobTitle: "",
  marketingRegionId: "",
};

export const AddNewRecipientUserDialog = ({
  open,
  handleClose,
  refreshList,
  regionsOptions = {},
  refreshRegionsOptions = () => null,
  recipientName,
  recipientId = null,
}) => {
  const { createApiCall } = useCreateApiCall();
  const dispatch = useAppDispatch();
  const { showWarning, userData } = useContext(GlobalContext);
  const [isAddNewRegionDialogOpen, setIsAddNewRegionDialogOpen] =
    useState(false);

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: defaultFormData,
  });

  const handleForcedClose = () => {
    reset(defaultFormData);
    handleClose();
  };

  const onSubmit = createApiCall(async () => {
    const user = getValues();

    const body = {
      user: {
        ...user,
        // @ts-ignore
        organizationId: recipientId ?? userData?.organizationId,
        firstName: user.firstName.trim(),
        lastName: user.lastName.trim(),
        jobTitle: user.jobTitle.trim(),
      },
      recipientName,
    };

    const result = await API.put("restApi", "/users", {
      body,
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            User added successfully
          </Typography>
        </Stack>
      ),
      content: <></>,
    });

    refreshList();
    handleForcedClose();
  });

  const closeAndReset = useCloseAndReset(handleForcedClose, isDirty);

  const password = useRef("");
  password.current = watch("password", "");

  const currentRole = watch("role");

  useEffect(() => {
    if (currentRole !== "Marketer") {
      setValue("marketingRegionId", "", {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRole]);

  return (
    <DialogWindow
      open={open}
      withCloseButton
      handleClose={closeAndReset}
      title={"Add New User"}
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack mt={2} gap={2}>
            <Stack direction={"row"} justifyContent={"space-between"} gap={2}>
              <Stack alignItems={"flex-start"} flex={1}>
                <InputLabel htmlFor="role">
                  Role <RequiredMark />
                </InputLabel>
                <FormSelectField
                  id="role"
                  name="role"
                  control={control}
                  options={{
                    Admin: "Admin",
                    Marketer: "Marketer",
                    Scheduler: "Scheduler",
                  }}
                  withNone={false}
                  rules={{ required }}
                  sx={{ textAlign: "left" }}
                />
              </Stack>
            </Stack>
            <Stack direction={"row"} gap={2}>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="firstName">
                  First name <RequiredMark />
                </InputLabel>
                <FormTextField
                  id="firstName"
                  name="firstName"
                  control={control}
                  rules={nameValidation}
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="lastName">
                  Last name <RequiredMark />
                </InputLabel>
                <FormTextField
                  id="lastName"
                  name="lastName"
                  control={control}
                  rules={nameValidation}
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack>
            </Stack>
            {["Marketer", "Scheduler"].includes(currentRole) && (
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="phone">
                  Phone <RequiredMark />
                </InputLabel>
                <FormMaskedTextField
                  id="phone"
                  name="phone"
                  control={control}
                  rules={phoneNumberValidation}
                  mask="999-999-9999"
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack>
            )}
            {currentRole === "Marketer" && (
              <Stack direction={"row"} gap={2} alignItems={"flex-end"}>
                <Stack alignItems={"flex-start"} flex="1">
                  <InputLabel htmlFor="marketingRegionId">
                    Marketing Region
                  </InputLabel>
                  <FormSelectField
                    id="marketingRegionId"
                    name="marketingRegionId"
                    control={control}
                    options={regionsOptions}
                  />
                </Stack>
                <Button onClick={() => setIsAddNewRegionDialogOpen(true)}>
                  Add new region
                </Button>
              </Stack>
            )}
            <Stack alignItems={"flex-start"}>
              <InputLabel htmlFor="email">
                Email <RequiredMark />
              </InputLabel>
              <FormTextField
                id="email"
                name="email"
                control={control}
                rules={emailValidation}
                inputProps={{
                  autocomplete: "off",
                  form: {
                    autocomplete: "off",
                  },
                }}
              />
            </Stack>
            <Stack direction={"row"} gap={2}>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="password">
                  Create password <RequiredMark />
                </InputLabel>
                <FormTextField
                  id="password"
                  name="password"
                  control={control}
                  rules={passwordValidation}
                  type={isPasswordVisible ? "text" : "password"}
                  InputProps={{
                    autoComplete: "new-password",
                  }}
                  inputProps={{
                    autocomplete: "new-password",
                    form: {
                      autocomplete: "new-password",
                    },
                  }}
                />
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="password_repeat">
                  Repeat password <RequiredMark />
                </InputLabel>
                <FormTextField
                  id="password_repeat"
                  name="password_repeat"
                  control={control}
                  rules={getPasswordConfirmValidation(password.current)}
                  type={isPasswordVisible ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        color="info"
                        onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                      >
                        {isPasswordVisible ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    ),
                    autoComplete: "new-password",
                  }}
                  inputProps={{
                    autocomplete: "new-password",
                    form: {
                      autocomplete: "new-password",
                    },
                  }}
                />
              </Stack>
            </Stack>
          </Stack>
          <AddNewRegionDialog
            open={isAddNewRegionDialogOpen}
            handleClose={() => setIsAddNewRegionDialogOpen(false)}
            refreshList={refreshRegionsOptions}
          />
        </form>
      }
      actions={
        <Stack
          direction={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"100%"}
          gap={2}
          mb={2}
        >
          <Box ml={2} sx={{ color: "gray", fontSize: 14 }}>
            <RequiredMark /> These fields are required
          </Box>
          <Button variant="contained" onClick={handleSubmit(onSubmit)}>
            Add New User
          </Button>
        </Stack>
      }
    />
  );
};
